import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Heart, MessageCircle, Star, User } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { useWebSocket } from "@/contexts/WebSocketContext";
import Header from "@/components/Header";

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications: wsNotifications, setNotifications: setWsNotifications } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Merge WebSocket notifications with fetched notifications
  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications((prev) => {
        const newNotifs = wsNotifications.filter(
          (wn) => !prev.find((p) => p.notification_id === wn.notification_id)
        );
        return [...newNotifs, ...prev];
      });
    }
  }, [wsNotifications]);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/notifications`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "PUT",
        credentials: "include",
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      toast.error("Failed to update notification");
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: "PUT",
        credentials: "include",
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    
    // Navigate based on notification type
    switch (notification.type) {
      case "match":
        navigate("/matches");
        break;
      case "message":
        if (notification.data?.match_id) {
          navigate(`/chat/${notification.data.match_id}`);
        }
        break;
      case "superlike":
      case "favorite":
        if (notification.data?.user_id) {
          navigate(`/profile/${notification.data.user_id}`);
        }
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "match":
        return <Heart className="w-6 h-6 text-[#FF66B2]" fill="currentColor" />;
      case "superlike":
        return <Star className="w-6 h-6 text-[#FFD700]" fill="currentColor" />;
      case "message":
        return <MessageCircle className="w-6 h-6 text-[#00CC99]" />;
      case "favorite":
        return <Star className="w-6 h-6 text-[#FF4D4D]" />;
      case "friend_online":
        return <User className="w-6 h-6 text-[#00CC99]" />;
      default:
        return <Bell className="w-6 h-6 text-[#4A4A4A]" />;
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-[#FFF5E1]">
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="notifications-title">
            Notifications
          </h1>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="brutal-btn bg-white py-2 text-sm"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="brutal-card animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 w-32"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="brutal-card text-center py-16" data-testid="no-notifications">
            <Bell className="w-16 h-16 mx-auto mb-4 text-[#4A4A4A]" />
            <h2 className="text-2xl font-bold mb-2">No notifications</h2>
            <p className="text-[#4A4A4A]">You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification, index) => (
              <motion.div
                key={notification.notification_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleNotificationClick(notification)}
                className={`brutal-card cursor-pointer flex items-start gap-4 ${
                  !notification.read ? "bg-[#E8E6F8]" : "bg-white"
                }`}
                data-testid={`notification-${notification.notification_id}`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white border-2 border-black flex-shrink-0">
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`font-bold ${!notification.read ? "text-black" : "text-[#4A4A4A]"}`}>
                      {notification.title}
                    </h3>
                    {!notification.read && (
                      <span className="w-3 h-3 bg-[#FF4D4D] rounded-full flex-shrink-0 mt-1"></span>
                    )}
                  </div>
                  <p className="text-[#4A4A4A] text-sm">{notification.body}</p>
                  <p className="text-xs text-[#4A4A4A] mt-1">
                    {new Date(notification.created_at).toLocaleString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
